/**
 * React hook for Lottie animation lifecycle management.
 *
 * Handles:
 * - prefers-reduced-motion detection
 * - IndexedDB cache-first fetching
 * - lottie-web player initialisation
 * - hover play/pause
 * - cleanup on unmount
 *
 */

import { useState, useEffect, useRef } from 'react';
import type { AnimationItem } from 'lottie-web';
import { useReducedMotion } from '@/hooks/useReducedMotion';

import { lottieCache } from './lottie-cache';
import { loadLottieSvgPlayer } from './lottie-player';
import type { LottieAnimationData } from './lottie-types';
interface UseLottieConfig {
  /** Unicode codepoint to load (e.g. "1f600"). */
  codepoint: string;
  /** Ref to the container <div> that receives the animation. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Start playing immediately. @default false */
  autoplay?: boolean;
  /** Loop the animation continuously. @default false */
  loop?: boolean;
  /** Play animation only while hovering the container. @default true */
  playOnHover?: boolean;
  /** Replay animation from start every N milliseconds. 0 = disabled. @default 0 */
  replayInterval?: number;
  /** Whether the hook should actually load and play. @default true */
  enabled?: boolean;
}

interface UseLottieReturn {
  isLoaded: boolean;
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  stop: () => void;
  error: string | null;
  prefersReducedMotion: boolean;
}

/** Manage Lottie animation lifecycle: fetch, cache, render, and hover play/pause. */
export function useLottie({
  codepoint,
  containerRef,
  autoplay = false,
  loop = false,
  playOnHover = true,
  replayInterval = 0,
  enabled = true,
}: UseLottieConfig): UseLottieReturn {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const animRef = useRef<AnimationItem | null>(null);
  const prefersReducedMotion = useReducedMotion();

  // Load animation data and initialise lottie-web player
  useEffect(() => {
    if (!enabled) return;
    if (prefersReducedMotion) return;
    if (!containerRef.current) return;

    let cancelled = false;
    const container = containerRef.current;

    async function init() {
      try {
        // 1. Fetch animation data (cache-first)
        const animData: LottieAnimationData | null = await lottieCache.fetchAnimation(codepoint);

        if (cancelled || !animData) {
          if (!animData) setError('Animation not found');
          return;
        }

        // 2. Load the shared light SVG-only player.
        const lottie = await loadLottieSvgPlayer();

        if (cancelled) return;

        // 3. Initialise player
        const anim = lottie.loadAnimation({
          container,
          renderer: 'svg',
          loop,
          autoplay: autoplay && !playOnHover,
          animationData: animData,
        });

        animRef.current = anim;

        anim.addEventListener('DOMLoaded', () => {
          if (!cancelled) {
            setIsLoaded(true);
            if (autoplay && !playOnHover) setIsPlaying(true);
          }
        });

        anim.addEventListener('loopComplete', () => {
          if (!loop && !cancelled) setIsPlaying(false);
        });

        anim.addEventListener('complete', () => {
          if (!cancelled) setIsPlaying(false);
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load animation');
        }
      }
    }

    init();

    return () => {
      cancelled = true;
      if (animRef.current) {
        animRef.current.destroy();
        animRef.current = null;
      }
      setIsLoaded(false);
      setIsPlaying(false);
    };
  }, [codepoint, containerRef, autoplay, loop, playOnHover, prefersReducedMotion, enabled]);

  // Periodic replay — restarts animation every `replayInterval` ms
  useEffect(() => {
    if (!replayInterval || replayInterval <= 0 || prefersReducedMotion) return;
    // Play once immediately when loaded
    const anim = animRef.current;
    if (anim && isLoaded) {
      anim.goToAndPlay(0);
      setIsPlaying(true);
    }
    const id = setInterval(() => {
      const a = animRef.current;
      if (a) {
        a.goToAndPlay(0);
        setIsPlaying(true);
      }
    }, replayInterval);
    return () => clearInterval(id);
  }, [replayInterval, isLoaded, prefersReducedMotion]);

  // Hover play / pause
  useEffect(() => {
    if (!playOnHover || prefersReducedMotion) return;
    const container = containerRef.current;
    if (!container) return;

    const handleEnter = () => {
      const anim = animRef.current;
      if (anim) {
        anim.goToAndPlay(0);
        setIsPlaying(true);
      }
    };

    const handleLeave = () => {
      const anim = animRef.current;
      if (anim) {
        anim.goToAndStop(0);
        setIsPlaying(false);
      }
    };

    container.addEventListener('mouseenter', handleEnter);
    container.addEventListener('mouseleave', handleLeave);

    return () => {
      container.removeEventListener('mouseenter', handleEnter);
      container.removeEventListener('mouseleave', handleLeave);
    };
  }, [playOnHover, containerRef, prefersReducedMotion]);
  function play() {
    animRef.current?.play();
    setIsPlaying(true);
  }

  function pause() {
    animRef.current?.pause();
    setIsPlaying(false);
  }

  function stop() {
    animRef.current?.goToAndStop(0);
    setIsPlaying(false);
  }

  return { isLoaded, isPlaying, play, pause, stop, error, prefersReducedMotion };
}
